import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  VStack,
  Heading,
  Text,
  Box,
  useColorMode,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Checkbox,
  Badge,
  Progress,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  HStack,
} from "@chakra-ui/react";
import { brandConfig, getColor } from "../../brandConfig";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

interface ChecklistItem {
  id: string;
  text: string;
  priority: "high" | "medium" | "low";
}

interface ChecklistCategory {
  name: string;
  items: ChecklistItem[];
}

const categories: ChecklistCategory[] = [
  {
    name: "Port & Shipping Data",
    items: [
      { id: "port-1", text: "How many crude oil tankers landed at Port of Brisbane in last 2 months", priority: "high" },
      { id: "port-2", text: "Daily diesel import volumes through Brisbane specifically", priority: "high" },
      { id: "port-3", text: "MarineTraffic API -- evaluate free tier for live vessel tracking", priority: "medium" },
      { id: "port-4", text: "QShips portal (qships.tmr.qld.gov.au) -- extract Queensland vessel data", priority: "medium" },
      { id: "port-5", text: "Port of Brisbane published vessel schedules", priority: "medium" },
    ],
  },
  {
    name: "Fuel Reserves & Storage",
    items: [
      { id: "fuel-1", text: "Liquid Fuels Gateway data analysis (Dept of Climate Change & Energy)", priority: "high" },
      { id: "fuel-2", text: "Queensland's fuel reserve storage locations (exact facilities)", priority: "high" },
      { id: "fuel-3", text: "State-by-state reserve days comparison", priority: "medium" },
      { id: "fuel-4", text: "Lytton refinery current output vs QLD demand", priority: "medium" },
    ],
  },
  {
    name: "Cost & Economics",
    items: [
      { id: "cost-1", text: "Cost comparison: domestic refining vs Singapore-refined imports", priority: "high" },
      { id: "cost-2", text: "Current diesel price trajectory and war premium", priority: "medium" },
      { id: "cost-3", text: "Shipping cost differential: Hormuz route vs US Pacific route", priority: "medium" },
    ],
  },
  {
    name: "Japan & Global",
    items: [
      { id: "japan-1", text: "Japan's emergency fuel sourcing responses post-Hormuz crisis", priority: "high" },
      { id: "japan-2", text: "Trump administration pressure on Japan to buy US energy", priority: "medium" },
      { id: "japan-3", text: "Russia ESPO pipeline current volumes to Japan", priority: "medium" },
      { id: "japan-4", text: "South Korea/Taiwan refinery capacity available for AU/JP", priority: "medium" },
    ],
  },
  {
    name: "Policy & Citizen Action",
    items: [
      { id: "policy-1", text: "AU 28-day reserve vs IEA 90-day obligation -- compliance gap", priority: "high" },
      { id: "policy-2", text: "What ordinary Australians can do to prepare", priority: "medium" },
      { id: "policy-3", text: "Political pressure points for higher strategic reserve targets", priority: "medium" },
      { id: "policy-4", text: "Domestic refinery investment advocacy", priority: "low" },
    ],
  },
  {
    name: "Supply Chain Tracking Tool",
    items: [
      { id: "tool-1", text: "Build prototype map with Mapbox/Leaflet showing tanker positions", priority: "medium" },
      { id: "tool-2", text: "n8n workflow to poll MarineTraffic API on schedule", priority: "medium" },
      { id: "tool-3", text: "Running counter of Australia's reserve days ticking down", priority: "low" },
      { id: "tool-4", text: "Colour-coded vessel status: on schedule, delayed, diverted, stopped", priority: "low" },
    ],
  },
];

const STORAGE_KEY = "energy-research-checklist";

const priorityColor = (priority: string) => {
  switch (priority) {
    case "high": return "red";
    case "medium": return "orange";
    case "low": return "gray";
    default: return "gray";
  }
};

const ResearchChecklist: React.FC = () => {
  const { colorMode } = useColorMode();
  const bgMain = getColor("background.main", colorMode);
  const textPrimary = getColor("text.primary", colorMode);
  const surfaceBg = brandConfig.colors.background.surface;
  const borderLight = brandConfig.colors.border.light;
  const textSecondary = brandConfig.colors.text.secondary;

  const [checked, setChecked] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(checked));
  }, [checked]);

  const toggleItem = (id: string) => {
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const totalItems = categories.reduce((sum, cat) => sum + cat.items.length, 0);
  const checkedCount = Object.values(checked).filter(Boolean).length;
  const progressPercent = totalItems > 0 ? (checkedCount / totalItems) * 100 : 0;

  return (
    <Box bg={bgMain} minHeight="100vh" display="flex" flexDirection="column">
      <NavbarWithCallToAction />

      <Container maxW="container.lg" py={10} flex="1">
        <VStack spacing={6} align="stretch">
          <Breadcrumb fontSize="sm" color={textSecondary}>
            <BreadcrumbItem>
              <BreadcrumbLink as={RouterLink} to="/energy-research">
                Energy Research
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink>Research Checklist</BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>

          <Heading size="lg" color="#dc2626">
            Research Checklist
          </Heading>
          <Text color={textSecondary} fontSize="sm">
            All the questions still to research -- organised by topic. Progress saved locally.
          </Text>

          {/* Progress bar */}
          <Box p={4} bg={surfaceBg} borderRadius="lg" border="1px solid" borderColor={borderLight}>
            <HStack justify="space-between" mb={2}>
              <Text fontSize="sm" color={textPrimary} fontWeight="bold">
                Research Progress
              </Text>
              <Text fontSize="sm" color={textSecondary}>
                {checkedCount} of {totalItems} items researched
              </Text>
            </HStack>
            <Progress
              value={progressPercent}
              colorScheme="red"
              borderRadius="md"
              size="sm"
            />
          </Box>

          <Accordion allowMultiple defaultIndex={categories.map((_, i) => i)}>
            {categories.map((category) => {
              const catChecked = category.items.filter((item) => checked[item.id]).length;
              return (
                <AccordionItem
                  key={category.name}
                  border="1px solid"
                  borderColor={borderLight}
                  borderRadius="lg"
                  mb={3}
                  bg={surfaceBg}
                >
                  <AccordionButton py={4} _hover={{ bg: "transparent" }}>
                    <Box flex="1" textAlign="left">
                      <HStack spacing={2}>
                        <Heading size="sm" color={textPrimary}>
                          {category.name}
                        </Heading>
                        <Badge colorScheme={catChecked === category.items.length ? "green" : "gray"} fontSize="2xs">
                          {catChecked}/{category.items.length}
                        </Badge>
                      </HStack>
                    </Box>
                    <AccordionIcon color={textSecondary} />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    <VStack align="stretch" spacing={2}>
                      {category.items.map((item) => (
                        <HStack
                          key={item.id}
                          p={2}
                          borderRadius="md"
                          _hover={{ bg: "rgba(255,255,255,0.03)" }}
                        >
                          <Checkbox
                            isChecked={!!checked[item.id]}
                            onChange={() => toggleItem(item.id)}
                            colorScheme="red"
                          />
                          <Text
                            fontSize="sm"
                            color={checked[item.id] ? textSecondary : textPrimary}
                            textDecoration={checked[item.id] ? "line-through" : "none"}
                            flex="1"
                          >
                            {item.text}
                          </Text>
                          <Badge colorScheme={priorityColor(item.priority)} fontSize="2xs">
                            {item.priority}
                          </Badge>
                        </HStack>
                      ))}
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
              );
            })}
          </Accordion>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default ResearchChecklist;
